/**
 * LeadContactInfoCard - Sidebar card showing a lead's emails, phones (click-to-call/SMS) and social links.
 * Extracted verbatim from LeadDetailPage.jsx (behavior unchanged); state/handlers passed as props.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Lock, Mail, Phone, XCircle, Ban, PhoneCall, Loader2, MessageSquare, Linkedin, Briefcase, Cpu } from 'lucide-react';
import { formatPhone } from '../../utils/formatPhone';

export default function LeadContactInfoCard({ lead, leadCompany, canCommunicate, telnyx, comms }) {
  return (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Contact Information</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {lead._contact_masked && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Lock className="h-4 w-4 flex-shrink-0" />
                  <span>Contact details are visible only to this lead's owner.</span>
                </div>
              )}
              {lead.work_email && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4 text-indigo-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Work Email</p>
                    <a href={'mailto:' + lead.work_email} className="text-sm text-indigo-600 hover:underline">{lead.work_email}</a>
                  </div>
                </div>
              )}
              {lead.personal_email && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4 text-slate-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Personal Email</p>
                    <a href={'mailto:' + lead.personal_email} className="text-sm text-slate-700">{lead.personal_email}</a>
                  </div>
                </div>
              )}
              {(lead.mobile_phone || lead.work_direct_phone || lead.work_company_phone) && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0"><Phone className="h-4 w-4 text-green-600" /></div>
                  <div className="space-y-1">
                    {lead.work_direct_phone && (
                      <div>
                        <p className="text-xs text-slate-500">Work Direct</p>
                        {lead.dnc || lead.bad_number ? (
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            {formatPhone(lead.work_direct_phone)} 
                            {lead.bad_number && <XCircle className="h-3 w-3 text-red-500" title="Bad Number" />}
                            {lead.dnc && <Ban className="h-3 w-3 text-red-400" title="Do Not Call" />}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => telnyx.handleClickToCall(lead.work_direct_phone)} disabled={telnyx.callingNumber === lead.work_direct_phone || !canCommunicate} className={`text-sm flex items-center gap-1 ${canCommunicate ? 'text-slate-700 hover:text-green-600 hover:underline' : 'text-slate-400 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can call" : telnyx.telnyxStatus.configured ? "Click to call via Telnyx" : "Click to dial"}>
                              {telnyx.callingNumber === lead.work_direct_phone ? (<><Loader2 className="h-3 w-3 animate-spin" /> Calling...</>) : (<>{formatPhone(lead.work_direct_phone)} <PhoneCall className="h-3 w-3" /></>)}
                            </button>
                            <button onClick={() => { comms.setSmsForm(f => ({ ...f, to: lead.work_direct_phone })); comms.setSmsDialogOpen(true); }} disabled={!canCommunicate} className={`p-1 rounded ${canCommunicate ? 'hover:bg-green-100 text-green-600 hover:text-green-700' : 'text-slate-300 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can SMS" : "Send SMS"} data-testid="work-direct-sms-btn">
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {lead.mobile_phone && (
                      <div>
                        <p className="text-xs text-slate-500">Mobile</p>
                        {lead.dnc || lead.bad_number ? (
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            {formatPhone(lead.mobile_phone)}
                            {lead.bad_number && <XCircle className="h-3 w-3 text-red-500" title="Bad Number" />}
                            {lead.dnc && <Ban className="h-3 w-3 text-red-400" title="Do Not Call" />}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => telnyx.handleClickToCall(lead.mobile_phone)} disabled={telnyx.callingNumber === lead.mobile_phone || !canCommunicate} className={`text-sm flex items-center gap-1 ${canCommunicate ? 'text-slate-700 hover:text-green-600 hover:underline' : 'text-slate-400 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can call" : telnyx.telnyxStatus.configured ? "Click to call via Telnyx" : "Click to dial"}>
                              {telnyx.callingNumber === lead.mobile_phone ? (<><Loader2 className="h-3 w-3 animate-spin" /> Calling...</>) : (<>{formatPhone(lead.mobile_phone)} <PhoneCall className="h-3 w-3" /></>)}
                            </button>
                            <button onClick={() => { comms.setSmsForm(f => ({ ...f, to: lead.mobile_phone })); comms.setSmsDialogOpen(true); }} disabled={!canCommunicate} className={`p-1 rounded ${canCommunicate ? 'hover:bg-green-100 text-green-600 hover:text-green-700' : 'text-slate-300 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can SMS" : "Send SMS"} data-testid="mobile-sms-btn">
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {lead.work_company_phone && (
                      <div>
                        <p className="text-xs text-slate-500">Company</p>
                        {lead.dnc || lead.bad_number ? (
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            {formatPhone(lead.work_company_phone)}
                            {lead.bad_number && <XCircle className="h-3 w-3 text-red-500" title="Bad Number" />}
                            {lead.dnc && <Ban className="h-3 w-3 text-red-400" title="Do Not Call" />}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => telnyx.handleClickToCall(lead.work_company_phone)} disabled={telnyx.callingNumber === lead.work_company_phone || !canCommunicate} className={`text-sm flex items-center gap-1 ${canCommunicate ? 'text-slate-700 hover:text-green-600 hover:underline' : 'text-slate-400 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can call" : telnyx.telnyxStatus.configured ? "Click to call via Telnyx" : "Click to dial"}>
                              {telnyx.callingNumber === lead.work_company_phone ? (<><Loader2 className="h-3 w-3 animate-spin" /> Calling...</>) : (<>{formatPhone(lead.work_company_phone)} <PhoneCall className="h-3 w-3" /></>)}
                            </button>
                            <button onClick={() => { comms.setSmsForm(f => ({ ...f, to: lead.work_company_phone })); comms.setSmsDialogOpen(true); }} disabled={!canCommunicate} className={`p-1 rounded ${canCommunicate ? 'hover:bg-green-100 text-green-600 hover:text-green-700' : 'text-slate-300 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can SMS" : "Send SMS"}>
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {lead.linkedin_url && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"><Linkedin className="h-4 w-4 text-blue-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">LinkedIn</p>
                    <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">View Profile</a>
                  </div>
                </div>
              )}
              {lead.x_twitter_url && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold">𝕏</span></div>
                  <div>
                    <p className="text-xs text-slate-500">X/Twitter</p>
                    <a href={lead.x_twitter_url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">View Profile</a>
                  </div>
                </div>
              )}
              {lead.linkedin_url_x && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0"><Linkedin className="h-4 w-4 text-indigo-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">LinkedIn URL-X</p>
                    <a href={lead.linkedin_url_x} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">View Profile</a>
                  </div>
                </div>
              )}
              {leadCompany?.ats_used && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0"><Briefcase className="h-4 w-4 text-orange-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">ATS Used</p>
                    <p className="text-sm text-slate-700">{leadCompany.ats_used}</p>
                  </div>
                </div>
              )}
              {leadCompany?.ai_voice_using && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0"><Cpu className="h-4 w-4 text-violet-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">AI Voice</p>
                    <p className="text-sm text-slate-700">{leadCompany.ai_voice_using}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
  );
}
